import {Slide} from '@aboutyou/transitions';
import React from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES} from '../../../constants/typography';

const Theme = {
  light: {
    fontColor: COLORS.Black,
    backgroundColor: COLORS.White,
  },
  dark: {
    fontColor: COLORS.White,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
};

export interface TooltipProps {
  show?: boolean;
  label: string;
  placement?: 'top-center' | 'bottom-center';
  showChevron?: boolean;
  animated?: boolean;
  padding?: 'small' | 'large';
  borderColor?: keyof typeof COLORS;
  backgroundColor?: string;
  theme?: 'light' | 'dark';
}

interface ControlledTooltipProps extends TooltipProps {
  show: boolean;
}

const UncontrolledTooltip: React.FC<
  React.PropsWithChildren<Omit<TooltipProps, 'show'>>
> = props => {
  const {children, showChevron, placement = 'top-center', ...rest} = props;
  const childRef = React.useRef<HTMLElement>(null);
  const [isTooltipShown, setIsTooltipShown] = React.useState(false);
  const [styles, setStyles] = React.useState<
    {top: number; left: number} | undefined
  >(undefined);

  return (
    <>
      {React.Children.map(children, child => {
        return React.cloneElement(child as any, {
          onMouseEnter: () => {
            if (childRef.current) {
              setIsTooltipShown(true);
              setStyles(getTooltipStyles(childRef.current, placement));
            }

            (child as any).props.onMouseEnter?.();
          },
          onMouseLeave: () => {
            setIsTooltipShown(false);

            (child as any).props.onMouseLeave?.();
          },
          ref: childRef,
        });
      })}
      <TooltipBody
        show={isTooltipShown}
        styles={styles}
        showChevron={getChevronStyles(showChevron, placement)}
        {...rest}
      />
    </>
  );
};

const ControlledTooltip: React.FC<
  React.PropsWithChildren<ControlledTooltipProps>
> = props => {
  const {
    show,
    children,
    showChevron,
    placement = 'top-center',
    ...rest
  } = props;

  const childRef = React.useRef<HTMLElement>(null);
  const styles = childRef.current
    ? getTooltipStyles(childRef.current, placement)
    : undefined;

  return (
    <>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as any, {
            ref: childRef,
          });
        }
        return null;
      })}
      <TooltipBody
        show={show}
        styles={styles}
        showChevron={getChevronStyles(showChevron, placement)}
        {...rest}
      />
    </>
  );
};
const TooltipBody = (props: {
  show: boolean;
  label: string;
  styles: {top: number; left: number} | undefined;
  showChevron: 'top' | 'bottom' | undefined;
  animated?: boolean;
  padding?: 'small' | 'large';
  theme?: 'light' | 'dark';
  borderColor?: keyof typeof COLORS;
  backgroundColor?: string;
}) => {
  const {
    show,
    label,
    styles,
    showChevron,
    animated,
    padding,
    theme = 'dark',
    borderColor,
    backgroundColor,
  } = props;
  return (
    <Slide
      mounted={show}
      duration={{mount: 350, unmount: 200}}
      direction="up"
      transformLength={30}
    >
      <Wrapper style={styles}>
        <TooltipAnimationWrapper $animated={animated}>
          <TooltipStyle
            data-testid="tooltip"
            $showChevron={showChevron}
            $backgroundColor={backgroundColor || Theme[theme].backgroundColor}
            $fontColor={Theme[theme].fontColor}
            $animated={animated}
            $padding={padding}
            $borderColor={borderColor}
            dangerouslySetInnerHTML={{__html: label}}
          />
        </TooltipAnimationWrapper>
      </Wrapper>
    </Slide>
  );
};

const Wrapper = styled.div`
  position: absolute;
  z-index: 1;
`;

const TooltipAnimationWrapper = styled.div<{$animated?: boolean}>`
  @keyframes bobbleUpAndDown {
    0% {
      transform: translateY(-10px);
    }
    100% {
      transform: translateY(0);
    }
  }
  position: absolute;
  pointer-events: none;
  min-width: max-content;
  ${({$animated}) =>
    $animated &&
    'animation: bobbleUpAndDown 1s ease-in-out infinite alternate;'}
`;

export const Tooltip: React.FC<
  React.PropsWithChildren<TooltipProps>
> = props => {
  const {show, ...rest} = props;
  if (show === undefined) {
    return <UncontrolledTooltip {...rest} />;
  }

  return <ControlledTooltip {...rest} show={show} />;
};

const getChevronStyles = (
  showChevron?: boolean,
  placement?: 'top-center' | 'bottom-center',
) => {
  if (showChevron) {
    return placement === 'top-center' ? 'bottom' : 'top';
  }
};

const getTooltipStyles = (
  childWrapper: HTMLElement,
  placement: 'top-center' | 'bottom-center',
) => {
  const top = childWrapper.offsetTop;
  const left = childWrapper.offsetLeft;
  const childWidth = childWrapper.offsetWidth;
  const childHeight = childWrapper.offsetHeight;

  const tooltipLeft = left + childWidth / 2;
  const tooltipTop = (() => {
    if (placement === 'top-center') {
      return top - 35; // 35 is the height of the tooltip + top margin
    }
    return top + childHeight + 10;
  })();

  return {
    top: tooltipTop,
    left: tooltipLeft,
  };
};

const TooltipStyle = styled.span<{
  $showChevron: 'top' | 'bottom' | undefined;
  $backgroundColor: string;
  $fontColor: string;
  $animated?: boolean;
  $padding?: 'small' | 'large';
  $borderColor?: keyof typeof COLORS;
}>`
  color: ${({$fontColor}) => $fontColor};
  font-size: ${FONT_SIZES.px12};
  background: ${({$backgroundColor}) => $backgroundColor};
  border-radius: 3px;
  padding: ${({$padding}) =>
    $padding === 'large' ? '14px 16px ' : '5px 10px 6px 10px'};
  transform: translateX(-50%);
  display: inline-block;
  box-shadow: rgb(0 0 0 / 4%) 0px 5px 12px 0px, rgb(0 0 0 / 5%) 0px 2px 4px 0px;
  max-width: 410px;
  text-align: center;
  line-height: 1.25rem;

  ${({$borderColor}) =>
    $borderColor && `border: 1px solid ${COLORS[$borderColor]};`};

  ${({$showChevron, $backgroundColor}) =>
    $showChevron &&
    css`
      &::after {
        content: '';
        display: block;
        width: 0;
        height: 0;
        position: absolute;
        margin-left: auto;
        margin-right: auto;
        left: 0;
        right: 0;
        border-${
          $showChevron === 'top' ? 'bottom' : 'top'
        }: 8px solid ${$backgroundColor};
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        top: ${$showChevron === 'top' ? '-8px' : '31px'};
        filter: drop-shadow(0px -3px 2px rgba(0,0,0,0.04));
      }
    `}
`;
